import {
    Component,
    EventEmitter,
    Input,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    computed,
    effect,
    inject,
    input,
    output,
} from '@angular/core'
import { MapAdvancedMarker, GoogleMap, GoogleMapsModule } from '@angular/google-maps'
import { WolseleyBranch } from 'src/app/shared/interfaces/wolseley/wolseley-branch.interface'
import { WolseleyBranchService } from 'src/app/core/services/wolseley/wolseley-branches.service'
import { Observable, forkJoin, of, switchMap } from 'rxjs'
import { WolseleyService } from 'src/app/core/services/wolseley/wolseley.service'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { TableModule } from '../../table/table.module'
import { WolseleyStock2 } from 'src/app/shared/interfaces/wolseley/wolseley-stock2.interface'
import { CommonModule, DatePipe } from '@angular/common'
import { SwitchComponent } from '../../form-elements/switch/switch.component'
import { toSignal } from '@angular/core/rxjs-interop'

interface MapMarkerWithOptions {
    position: google.maps.LatLngLiteral
    marker: google.maps.marker.AdvancedMarkerElement
    branch: WolseleyBranch
}

@Component({
    selector: 'sf-wolseley-stock-check',
    imports: [
        RouterModule,
        TableModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        GoogleMapsModule,
        SwitchComponent,
        DatePipe,
    ],
    templateUrl: './wolseley-stock-check.component.html',
    styleUrl: './wolseley-stock-check.component.scss',
})
export class WolseleyStockCheckComponent {
    private fb = inject(FormBuilder)
    private wolseleyBranchService = inject(WolseleyBranchService)
    private wolseleyService = inject(WolseleyService)

    @ViewChildren(MapAdvancedMarker) markersList!: QueryList<MapAdvancedMarker>
    @ViewChild(GoogleMap) map!: google.maps.Map
    bounds!: google.maps.LatLngBounds
    center: google.maps.LatLngLiteral = { lat: 52, lng: 1 }
    zoom = 4
    markers: MapMarkerWithOptions[] = []

    branches$!: Observable<WolseleyBranch[]>
    wolseleyStock!: WolseleyStock2

    products = input.required<{ productCode: string; quantity: number }[]>()
    deliveryPostcode = input<string | null>()
    @Input() showEditFavourites = false
    onStockChange = output<WolseleyStock2>()
    @Output() onNavigate = new EventEmitter<void>()

    form: FormGroup = this.fb.group({
        showUnavailableProducts: this.fb.control<boolean>(false),
    })

    formChanges = toSignal(this.form.valueChanges)
    showUnavailableProducts = computed(() => this.formChanges().showUnavailableProducts)

    constructor() {
        effect(() => {
            const deliveryPostcode = this.deliveryPostcode() ?? null
            const currentProducts = this.products()

            if (currentProducts && currentProducts.length) {
                this.bounds = new google.maps.LatLngBounds()

                const onlyFavouriteBranches = true

                this.wolseleyBranchService
                    .getBranches(onlyFavouriteBranches)
                    .pipe(
                        switchMap((branches) => {
                            const products = this.products()
                            const favouriteBranchCodes = branches
                                .filter((branch) => branch.isFavourite)
                                .map((branch) => branch.branchCode)

                            const stockCheck$ = this.wolseleyService.getWolseleyStock2({
                                branchCodes: favouriteBranchCodes,
                                products: products,
                                postCode: deliveryPostcode,
                                includeBranchDelivery: deliveryPostcode ? true : false,
                                includeExpressDelivery: false,
                            })

                            return forkJoin([of(branches), stockCheck$])
                        })
                    )
                    .subscribe(([branches, stockCheck]) => {
                        this.wolseleyStock = stockCheck
                        this.onStockChange.emit(stockCheck)

                        const stockAvailability = stockCheck.dataArea.productAvailability.stockAvailability

                        const branchUnavailableProducts = new Map<string, number>(
                            stockAvailability.map((branch) => {
                                const unavailableProducts = branch.productDatas.filter(
                                    (product) =>
                                        (deliveryPostcode && product.earliestDeliveryDate == null) ||
                                        product.earliestCollectionDate == null
                                )
                                return [branch.branchCode, unavailableProducts.length]
                            })
                        )

                        const anyFavourites = branches.some((branch) => branch.isFavourite)

                        branches.forEach((branch) => {
                            if (branch.latitude && branch.longitude && (!anyFavourites || branch.isFavourite)) {
                                this.bounds.extend({ lat: branch.latitude, lng: branch.longitude })
                            }
                        })

                        this.markers = branches
                            .filter((branch) => branch.latitude && branch.longitude)
                            .map((branch) => {
                                const hasUnavailable = branchUnavailableProducts.get(branch.branchCode) ?? 0
                                const pinColor = hasUnavailable ? '#dc3545' : '#198754'

                                const pinBackground = new google.maps.marker.PinElement({
                                    background: pinColor,
                                })

                                return {
                                    position: { lat: branch.latitude, lng: branch.longitude },
                                    marker: new google.maps.marker.AdvancedMarkerElement({
                                        gmpDraggable: false,
                                        title: branch.branchName,
                                        content: pinBackground.element,
                                        zIndex: branch.isFavourite ? 1 : 0,
                                    }),
                                    branch,
                                } as MapMarkerWithOptions
                            })

                        requestAnimationFrame(() => {
                            this.map?.fitBounds(this.bounds, { top: 50, bottom: 50, left: 50, right: 50 })
                        })
                    })
            }
        })
    }

    handleNavigate() {
        this.onNavigate.emit()
    }
}
